import api from "./base";
import { decamelize } from "humps";

/**
 * Re-key an object's top-level keys from camelCase back to snake_case,
 * without touching nested values. Code IDs like "hpd_registration" get
 * incorrectly camelized by the global axios interceptor.
 */
function restoreCodeIdKeys<T>(obj: Record<string, T>): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[decamelize(key)] = value;
  }
  return result;
}

export interface BuildingInput {
  address?: string | null;
  bin?: string | null;
}

/**
 * `unknown` is the fail-safe state for upstream-data outages: the
 * NYC Open Data fetch failed, was malformed, or stale beyond
 * tolerance, so we cannot determine eligibility. Distinct from
 * `not_eligible` (which is an authoritative negative) and from
 * `maybe` (which is "we have data, ask the owner a question").
 *
 * Conflating `unknown` and `not_eligible` was the FN-5 production
 * bug — see docs/chat-assistant/research/10-tracking-failsafe-architecture.md.
 */
export type CodeStatus = "eligible" | "not_eligible" | "maybe" | "unknown";

export interface CodeResult {
  status?: CodeStatus;
  reason: string;
  eligible?: boolean | null;
  needsOwnerInput?: boolean;
  ownerQuestionKey?: string;
  /** Next due date (ISO YYYY-MM-DD) when applicable */
  deadline?: string | null;
  /** Human-readable compliance schedule when no single deadline (e.g. "Annual", "Every 5 years") */
  schedule?: string | null;
}

/**
 * Coverage tier — what CarbonClear does for each code today.
 * Drives the badge + CTA on tracker tiles and portal advisory rows.
 *
 *   "live"          — full filing flow shipped (LL97, LL88)
 *   "in_development" — actively building, ~90 days out. Earn this label.
 *   "roadmap"       — committed to file, not scheduled
 *   "tracking_only" — we surface deadlines + requirements; customer files
 */
export type CoverageTier =
  | "live"
  | "in_development"
  | "roadmap"
  | "tracking_only";

/** Per-code coverage tier. Single source of truth for both /tracking and the customer portal. */
export const CODE_COVERAGE_TIERS: Record<string, CoverageTier> = {
  // Tier 1 — live filing flows
  ll97: "live",
  ll88: "live",
  // Tier 3 — committed to the roadmap
  ll152: "roadmap",
  // Tier 4 — tracking-only (default for everything else; explicit for clarity)
  ll84: "tracking_only",
  ll33: "tracking_only",
  ll87: "tracking_only",
  fc4016: "tracking_only",
  fc906: "tracking_only",
  bc907: "tracking_only",
  fc905121: "tracking_only",
  fc9035: "tracking_only",
  fc40421: "tracking_only",
  fc4017: "tracking_only",
  fc510: "tracking_only",
  fc908915: "tracking_only",
  fc904: "tracking_only",
  fc609: "tracking_only",
  fc5704: "tracking_only",
  fdny_periodic: "tracking_only",
  cof: "tracking_only",
  pa: "tracking_only",
  fisp: "tracking_only",
  ll126: "tracking_only",
  parapet_observation: "tracking_only",
  hpd_registration: "tracking_only",
  ll1_lead_paint: "tracking_only",
  bedbug_report: "tracking_only",
  self_closing_doors: "tracking_only",
  heat_hot_water: "tracking_only",
  required_postings: "tracking_only",
  hpd_egress_locks: "tracking_only",
  window_guards: "tracking_only",
  ll55_indoor_allergens: "tracking_only",
  elevator_cat1: "tracking_only",
  elevator_cat3: "tracking_only",
  elevator_cat5: "tracking_only",
  elevator_periodic_inspection: "tracking_only",
  elevator_single_plunger_brake: "tracking_only",
  boiler_low: "tracking_only",
  boiler_high: "tracking_only",
  dep_boiler_registration: "tracking_only",
  cooling_tower: "tracking_only",
};

/** Human-readable labels for code IDs */
export const CODE_LABELS: Record<string, string> = {
  ll97: "LL97 (Building Emissions)",
  ll84: "LL84 (Energy Benchmarking)",
  ll88: "LL88 (Lighting & Submetering)",
  ll33: "LL33 (Energy Efficiency Grade)",
  ll87: "LL87 (Energy Audit & Retro-Commissioning)",
  fc4016: "FC 401.6 (FEP Guide)",
  fc906: "FC 906 (Fire Extinguisher)",
  bc907: "BC 907 (Fire Alarm)",
  fc905121: "FC 905.12.1 (Standpipe)",
  fc9035: "FC 903.5 (Sprinkler)",
  fc40421: "FC 404.2.1 (FSEP)",
  fc4017: "FC 401.7 (Emergency Drills)",
  fc510: "FC 510 (ARCS)",
  fc908915: "FC 908/915 (CO and Gas Alarms)",
  fc904: "FC 904 (Cooking Hood Suppression)",
  fc609: "FC 609 (Cooking Duct Cleaning)",
  fc5704: "FC 5704 (Fuel-Oil Tank Compliance)",
  fdny_periodic: "FDNY Periodic Inspection",
  cof: "FDNY Certificate of Fitness (Operators)",
  pa: "FDNY Place of Assembly",
  fisp: "FISP / LL11 (Facade Inspection)",
  ll126: "LL126 (Parking Structure)",
  parapet_observation: "LL126 (Parapet Observation)",
  hpd_registration: "HPD Registration (Multiple Dwelling)",
  ll1_lead_paint: "LL1 (Lead Paint)",
  bedbug_report: "Bedbug Annual Report",
  self_closing_doors: "HPD Self-Closing Doors",
  heat_hot_water: "HPD Heat and Hot Water",
  required_postings: "HPD Required Postings",
  hpd_egress_locks: "HPD Egress and Locks",
  window_guards: "HPD Window Guards",
  ll55_indoor_allergens: "LL55 (Indoor Allergens)",
  elevator_cat1: "Elevator CAT1 (Annual Inspection)",
  elevator_periodic_inspection: "Elevator Periodic (Annual Inspection)",
  elevator_cat3: "Elevator CAT3 (Periodic Test – Hydraulic)",
  elevator_cat5: "Elevator CAT5 (Full Load Test – Traction)",
  elevator_single_plunger_brake: "Elevator Brake (Single Plunger Upgrade)",
  boiler_low: "Low-Pressure Boiler",
  boiler_high: "High-Pressure Boiler",
  dep_boiler_registration: "DEP Boiler Registration",
  cooling_tower: "Cooling Tower Certification",
  ll152: "LL152 (Gas Piping Periodic Inspection)",
};

/**
 * Code groups for tracking UI: expand to see codes in each group.
 *
 * Order is load-bearing — it drives the section order on the building
 * detail page. Per the LL87 flow redesign (frame 01): Carbon, DOB
 * Safety, Fire & Life Safety, Housing / HPD.
 */
export const CODE_GROUPS: { id: string; label: string; codeIds: string[] }[] = [
  {
    id: "carbon",
    label: "Carbon",
    codeIds: ["ll97", "ll84", "ll88", "ll33", "ll87"],
  },
  {
    id: "dob_safety",
    label: "DOB Safety",
    codeIds: [
      "fisp",
      "ll126",
      "parapet_observation",
      "ll152",
      "elevator_cat1",
      "elevator_periodic_inspection",
      "elevator_cat3",
      "elevator_cat5",
      "elevator_single_plunger_brake",
      "boiler_low",
      "boiler_high",
      "dep_boiler_registration",
      "cooling_tower",
    ],
  },
  {
    id: "fire_life_safety",
    label: "Fire & Life Safety",
    codeIds: [
      "fc4016",
      "fc906",
      "bc907",
      "fc905121",
      "fc9035",
      "fc40421",
      "fc4017",
      "fc510",
      "fc908915",
      "fc904",
      "fc609",
      "fc5704",
      "fdny_periodic",
      "cof",
      "pa",
    ],
  },
  {
    id: "housing_hpd",
    label: "Housing / HPD",
    codeIds: [
      "hpd_registration",
      "ll1_lead_paint",
      "bedbug_report",
      "self_closing_doors",
      "heat_hot_water",
      "required_postings",
      "hpd_egress_locks",
      "window_guards",
      "ll55_indoor_allergens",
    ],
  },
];

export interface TrackingResult {
  id?: string;
  addressInput: string | null;
  binInput: string | null;
  addressResolved: string | null;
  borough: string | null;
  bbl: string | null;
  bin: string | null;
  grossSqft: number | null;
  numBuildings: number | null;
  codes: Record<string, CodeResult>;
  error: string | null;
}

export interface CheckEligibilityResponse {
  results: TrackingResult[];
}

function fixTrackingResults(
  data: CheckEligibilityResponse,
): CheckEligibilityResponse {
  if (data.results) {
    for (const r of data.results) {
      if (r.codes) {
        r.codes = restoreCodeIdKeys(r.codes);
      }
    }
  }
  return data;
}

export const checkEligibility = (buildings: BuildingInput[]) =>
  api
    .post<CheckEligibilityResponse>("tracking/check-eligibility/", {
      buildings,
    })
    .then((res) => fixTrackingResults(res.data));

// ─── Locate (Add Building redesign — Step 1 multi-candidate typeahead) ───
//
// POST /api/tracking/locate/  body {query: <string>}
//
// Returns up to N=5 ranked candidate buildings for a typed query (address
// free-text OR a 7-digit BIN — the BE auto-routes). Companion to
// checkEligibility, but cheap enough to debounce per keystroke because
// it runs ONLY the geocode + PLUTO-batch + rank pass — no code checkers,
// no snapshot writes. Used by AddBuildingLocatePanel to render the
// "112 madison → 4 matches" dropdown so the user picks a building
// instead of trusting the geocoder ranker.
export interface LocateCandidate {
  bin: string | null;
  bbl: string | null;
  addressResolved: string | null;
  borough: string | null;
}
export interface LocateResponse {
  candidates: LocateCandidate[];
  error: string | null;
}

export const locate = (query: string): Promise<LocateResponse> =>
  api
    .post<LocateResponse>("tracking/locate/", { query })
    .then((res) => res.data);

// ─── Locate-detail (Add Building redesign — Step 2 per-field provenance) ──
//
// POST /api/tracking/locate-detail/  body {bin: <string>}
//
// After the user picks a candidate in the Locate panel, the Verify panel
// calls this to assemble every NYC source's value per identity field:
//   { grossFloorArea: { values: [<pill>, <pill>], has_conflict: false }, ... }
// + an overall confidence tier ("high" / "review" / "estimate" / "missing").
//
// The FE renders one pill per ``values[]`` entry. Multi-source agreement
// → "high" tier (green bar). Same-vocabulary divergence on a numeric field
// → "review" tier (amber bar, conflict picker). Single-source coverage
// → "estimate" tier (slate bar, "we'll improve this with your input").
export type ConfidenceTier = "high" | "review" | "estimate" | "missing";

export interface LocateDetailFieldPill {
  source: "pluto" | "ll84" | "cbl" | string;
  label: string;
  // Number for GFA/year; string for use/zip. The BE preserves the raw
  // type so the FE can pick the right formatter per field.
  value: number | string;
  // ISO timestamp the source row was fetched/read.
  fetchedAt: string;
}

export interface LocateDetailFieldSources {
  values: LocateDetailFieldPill[];
  hasConflict: boolean;
}

export interface LocateDetailResponse {
  bin: string;
  bbl: string | null;
  addressResolved: string | null;
  borough: string | null;
  fieldSources: Record<string, LocateDetailFieldSources>;
  confidence: ConfidenceTier;
  error: string | null;
}

export const locateDetail = (
  bin: string,
): Promise<LocateDetailResponse> =>
  api
    .post<LocateDetailResponse>("tracking/locate-detail/", { bin })
    .then((res) => res.data);

// ─── Inline identity attestation (portal building-detail page) ────────
//
// PATCH /api/tracking/portal/buildings/<id>/identity/
//
// Single-field attestation endpoint surfaced behind the pencil-icon
// inline editor on /portal/buildings/<id>/. Sibling to the Add
// Building flow's confirmed_* path — both stamp the same
// ``tracker_owner_attested`` provenance source. One field per save
// matches the pencil-edit interaction model (no batched drafts).
//
// Field names are the BuildingDetails column names verbatim (not the
// ``confirmed_*`` wire-rename Add Building uses) — the FE is editing
// the value as DISPLAYED, so the cleaner mapping is the column name.
export type IdentityField =
  | "grossFloorArea"
  | "primaryFunction"
  | "yearBuilt"
  | "postal_code";

/**
 * Canonical ENERGY STAR Portfolio Manager property types — the same
 * vocabulary LL84 uses and the only one the LL97 emissions-cap logic
 * recognizes. Sourced from the live NYC LL84 disclosure dataset (top
 * 25 categories cover >99% of NYC buildings); ordered by NYC
 * prevalence so the most common pick is at the top of the dropdown.
 *
 * Why hardcoded vs BE-fetched: the list changes maybe once a decade
 * (ESPM rev'd "Hotel" → "Hotel" in 2018 — that's the most recent
 * vocab change). A 30-item static list beats a network round-trip,
 * and the FE shows the user a stable picker even when the LL84 cache
 * is being warmed. If a customer's building is genuinely "Other," the
 * "Other" bucket below routes to the LL97 fallback cap.
 */
export const PRIMARY_FUNCTION_OPTIONS: readonly string[] = [
  "Multifamily Housing",
  "Office",
  "K-12 School",
  "College/University",
  "Hotel",
  "Retail Store",
  "Mixed Use Property",
  "Non-Refrigerated Warehouse",
  "Manufacturing/Industrial Plant",
  "Hospital (General Medical & Surgical)",
  "Medical Office",
  "Worship Facility",
  "Residence Hall/Dormitory",
  "Senior Living Community",
  "Pre-school/Daycare",
  "Library",
  "Police Station",
  "Fire Station",
  "Courthouse",
  "Distribution Center",
  "Self-Storage Facility",
  "Parking",
  "Restaurant",
  "Supermarket/Grocery Store",
  "Bank Branch",
  "Data Center",
  "Other - Lodging/Residential",
  "Other - Public Services",
  "Other - Recreation",
  "Other",
];

/**
 * PLUTO BldgClass → ENERGY STAR Property Type fallback map.
 *
 * Used to translate the city's 2-character DOF building classification
 * codes (e.g. ``O6``, ``D7``, ``R6``) into the ENERGY STAR vocabulary
 * the rest of the platform speaks. Only the first character is
 * meaningful here — the second digit denotes sub-type and rarely
 * changes the ESPM bucket.
 *
 * We hit this fallback only when LL84 has no row for the building
 * (LL84's ``PropType`` IS the canonical ESPM vocabulary, so when it's
 * present we use it verbatim). The map below covers ~95% of NYC
 * buildings; anything outside the map collapses to ``"Other"`` so
 * downstream LL97 caps fall through the platform's existing
 * "unknown property type" telemetry rather than silently picking a
 * wrong cap.
 *
 * Source: NYC DOF Building Classification Codes
 * (https://www.nyc.gov/assets/finance/jump/hlpbldgcode.html). Codes
 * are stable across PLUTO releases; the last vocab change was in 2015.
 */
const PLUTO_BLDG_CLASS_TO_ESPM: Record<string, string> = {
  A: "Other - Lodging/Residential", // 1-family dwelling
  B: "Multifamily Housing", // 2-family dwelling
  C: "Multifamily Housing", // walk-up apartment
  D: "Multifamily Housing", // elevator apartment
  E: "Non-Refrigerated Warehouse",
  F: "Manufacturing/Industrial Plant", // factory
  G: "Parking", // garage
  H: "Hotel",
  I: "Hospital (General Medical & Surgical)",
  J: "Other - Recreation", // theater
  K: "Retail Store",
  L: "Office", // loft (NYC convention treats L as office-equivalent)
  M: "Worship Facility",
  N: "Senior Living Community", // asylums and homes
  O: "Office",
  P: "Other - Public Services",
  Q: "Other - Recreation", // outdoor recreation
  R: "Multifamily Housing", // condominium
  S: "Mixed Use Property",
  T: "Other - Public Services", // transportation
  U: "Other - Public Services", // utility
  V: "Other", // vacant land
  W: "K-12 School",
  Y: "Other - Public Services", // selected government
  Z: "Other",
};

/**
 * Translate a PLUTO BldgClass code (e.g. ``"O6"``) into the canonical
 * ENERGY STAR Property Type label the ``PRIMARY_FUNCTION_OPTIONS``
 * dropdown speaks. Falls back to ``"Other"`` for unrecognized codes
 * so a typo or new sub-type can never select the wrong LL97 cap.
 */
export const plutoBldgClassToPropertyType = (bldgClass: string): string => {
  if (!bldgClass) return "Other";
  const letter = bldgClass.trim().charAt(0).toUpperCase();
  return PLUTO_BLDG_CLASS_TO_ESPM[letter] ?? "Other";
};

/**
 * Pick the canonical ESPM Property Type from a locate-detail
 * response. Prefer LL84's value (already in the canonical vocab) over
 * PLUTO's BldgClass code, since LL84 disclosures ARE owner-attested
 * annual filings — they speak ESPM-native, no translation needed.
 */
export const resolveCanonicalPrimaryFunction = (
  detail: LocateDetailResponse | null | undefined,
): string => {
  const values = detail?.fieldSources?.primaryFunction?.values ?? [];
  const ll84 = values.find((v) => v.source === "ll84");
  if (ll84 && typeof ll84.value === "string" && ll84.value.trim()) {
    return ll84.value.trim();
  }
  const pluto = values.find((v) => v.source === "pluto");
  if (pluto && typeof pluto.value === "string" && pluto.value.trim()) {
    return plutoBldgClassToPropertyType(pluto.value);
  }
  return "";
};

export interface IdentityPatchResponse {
  field: IdentityField;
  value: number | string | null;
  source: string | null;
  attestedAt: string | null;
  confidence: string;
  isOwnerAttested: boolean;
  // New chip state for the dashboard row — same vocab as the
  // dashboard's PortalBuildingMetrics.dataState. The FE uses this
  // to optimistically flip the chip / tilde without a refetch.
  dataState: "verified" | "needs_review" | "syncing" | "city_estimate";
}

export const patchBuildingIdentity = (
  buildingId: string,
  field: IdentityField,
  value: string | number,
  signerRelationship?: string,
): Promise<IdentityPatchResponse> =>
  api
    .patch<IdentityPatchResponse>(
      `tracking/portal/buildings/${buildingId}/identity/`,
      {
        field,
        value,
        ...(signerRelationship ? { signer_relationship: signerRelationship } : {}),
      },
    )
    .then((res) => res.data);

export const checkEligibilityFromFile = (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return api
    .post<CheckEligibilityResponse>("tracking/check-eligibility/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => fixTrackingResults(res.data));
};

// ── Tracker-owned building create (Phase 3.C) ────────────────────────
//
// POST /api/tracking/buildings/
//
// Two-modes of creation, distinguished by the presence of any
// ``confirmed_*`` field beyond the always-required ``confirmed_bbl``:
//
//   - Smart-confirmation path: owner typed values into the GFA / use /
//     year-built inputs and clicked "Confirm and add". The BE stamps
//     ``source_provenance`` with ``tracker_owner_attested`` for each
//     confirmed field. LL97 cell can immediately render an estimate.
//
//   - City-estimates path: owner clicked "Add with City estimates".
//     We omit the optional ``confirmed_*`` fields entirely; the BE
//     creates the building with ``source_provenance={}`` and the
//     dashboard renders a "Confirm details to estimate" CTA that
//     re-opens this dialog (Phase 3.A).
//
// The ``confirmed_bbl`` field is always sent because resolving an
// address to a BIN/BBL is the lowest-friction confirmation in the
// flow — accepting the resolved building IS confirming the BBL.
//
// Keys are snake_case verbatim to match the BE serializer; the axios
// request interceptor in @/api/base also decamelizes camelCase keys,
// but explicit snake_case avoids any ambiguity for IDs like nyc_bin
// (matches the existing ``createBuilding`` helper in @/api/building).
export interface CreateTrackingBuildingPayload {
  nyc_bin: string;
  confirmed_bbl: string;
  /** Picker-selected address label, persisted as Building.name. When
   * omitted, the BE wrapper sends a "New Building" placeholder and the
   * inner serializer falls back to the resolver-derived name — which
   * can be a different street face on the same BBL (B2). */
  name?: string;
  confirmed_gfa?: number;
  confirmed_primary_function?: string;
  confirmed_year_built?: number;
  confirmed_postal_code?: string;
  signer_relationship?: string;
  portfolio?: string | null;
}

/** Building shape returned by the BE on 201; loose-typed for now —
 * Phase 3.A's dashboard query owns the canonical building shape. */
export interface CreatedTrackingBuilding {
  id: string;
  name?: string;
  nycBin?: string;
  bbl?: string;
  [key: string]: unknown;
}

export const createTrackingBuilding = (
  payload: CreateTrackingBuildingPayload,
): Promise<CreatedTrackingBuilding> =>
  api
    .post<CreatedTrackingBuilding>("tracking/buildings/", payload)
    .then((res) => res.data);

// ── Compliance answers (server-side persistence) ──────────────────────
//
// Persisted owner answers for "needs review" questions (e.g. gas_service
// for LL152). The backend endpoint is per-building and scoped to the
// caller's company; a 404 means the building is not visible to the user.

export type ComplianceAnswerValue = "yes" | "no" | "unsure";

export interface ComplianceAnswer {
  questionKey: string;
  answer: ComplianceAnswerValue;
  answeredAt?: string | null;
}

export interface ComplianceAnswersResponse {
  answers: ComplianceAnswer[];
}

